/**
  * Login page with Google Sign-In
  */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuroraBackground from '../components/AuroraBackground';
import SEO from '../components/SEO';

const LoginPage: React.FC = () => {
    const { user, loading, signInWithGoogle, signInWithApple, error: authError } = useAuth();
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Get the redirect URL from query params (no default — absence means no specific redirect)
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirect');

    // Redirect if already logged in
    useEffect(() => {
        if (!loading && user) {
            navigate(redirectTo || '/sessions');
        }
    }, [user, loading, navigate, redirectTo]);

    const handleGoogleSignIn = async () => {
        setIsSigningIn(true);
        setError(null);
        try {
            await signInWithGoogle();
            // Navigation handled by useEffect
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') {
                setError(err.message || 'Sign-in failed. Please try again.');
            }
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleAppleSignIn = async () => {
        setIsSigningIn(true);
        setError(null);
        try {
            await signInWithApple();
            // Navigation handled by useEffect
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') {
                setError(err.message || 'Sign-in failed. Please try again.');
            }
        } finally {
            setIsSigningIn(false);
        }
    };

    if (loading) {
        return (
            <>
                <SEO
                    title="Sign In | Vibe Commenting"
                    description="Sign in to Vibe Commenting to start generating AI-powered Instagram comments and grow your engagement authentically."
                    path="/login"
                />
                <AuroraBackground />
                <div style={styles.container}>
                    <div style={styles.card}>
                        <div style={styles.spinner} />
                        <p style={styles.loadingText}>Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO
                title="Sign In | Vibe Commenting"
                description="Sign in to Vibe Commenting to start generating AI-powered Instagram comments and grow your engagement authentically."
                path="/login"
            />
            <AuroraBackground />
            <div style={styles.container}>
                <div style={styles.card}>
                {/* Logo */}
                <div style={styles.logo}>
                    <img src="/logo-white-192.png" alt="Vibe Commenting" style={styles.logoIcon} />
                    <span style={styles.logoText}>vibecommenting</span>
                </div>

                <h1 style={styles.title}>Welcome Back</h1>
                <p style={styles.subtitle}>
                    {redirectTo
                        ? 'Sign in to access this feature'
                        : 'Sign in to continue to Vibe Commenting'}
                </p>

                {/* Error message */}
                {(error || authError) && (
                    <div style={styles.errorBox}>
                        {error || authError}
                    </div>
                )}

                {/* Google Sign-In Button */}
                <button
                    onClick={handleGoogleSignIn}
                    disabled={isSigningIn}
                    style={{
                        ...styles.googleButton,
                        opacity: isSigningIn ? 0.7 : 1,
                        cursor: isSigningIn ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isSigningIn ? (
                        <div style={styles.buttonSpinner} />
                    ) : (
                        <svg style={styles.googleIcon} viewBox="0 0 24 24">
                            <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                        </svg>
                    )}
                    <span>{isSigningIn ? 'Signing in...' : 'Continue with Google'}</span>
                </button>

                {/* Apple Sign-In Button */}
                <button
                    onClick={handleAppleSignIn}
                    disabled={isSigningIn}
                    style={{
                        ...styles.appleButton,
                        opacity: isSigningIn ? 0.7 : 1,
                        cursor: isSigningIn ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isSigningIn ? (
                        <div style={styles.buttonSpinner} />
                    ) : (
                        <svg style={styles.appleIcon} viewBox="0 0 24 24">
                            <path
                                fill="#FFFFFF"
                                d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                            />
                        </svg>
                    )}
                    <span>{isSigningIn ? 'Signing in...' : 'Continue with Apple'}</span>
                </button>

                <p style={styles.terms}>
                    By signing in, you agree to our{' '}
                    <a href="/terms" style={styles.link}>Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" style={styles.link}>Privacy Policy</a>
                </p>

                <button
                    onClick={() => navigate(-1)}
                    style={styles.goBackButton}
                >
                    Go Back
                </button>
            </div>
        </div>
        </>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    logo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '32px',
    },
    logoIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    logoText: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#000000',
        letterSpacing: '-0.02em',
    },
    title: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '16px',
        color: '#666666',
        marginBottom: '32px',
    },
    errorBox: {
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '24px',
        color: '#ef4444',
        fontSize: '14px',
    },
    googleButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        background: '#7C3AED',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '14px 24px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    googleIcon: {
        width: '20px',
        height: '20px',
    },
    appleButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        background: '#000000',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '14px 24px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginTop: '12px',
    },
    appleIcon: {
        width: '20px',
        height: '20px',
    },
    buttonSpinner: {
        width: '20px',
        height: '20px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTopColor: '#FFFFFF',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    terms: {
        marginTop: '24px',
        fontSize: '12px',
        color: '#999999',
    },
    link: {
        color: '#7C3AED',
        textDecoration: 'underline',
    },
    goBackButton: {
        marginTop: '16px',
        background: 'transparent',
        border: 'none',
        color: '#7C3AED',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        padding: '8px 16px',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#7C3AED',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 16px',
    },
    loadingText: {
        color: '#666666',
        fontSize: '14px',
    },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default LoginPage;
